import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography, Collapse } from "@mui/material";
import { computeTurnCost, countTurnTokens } from "./tokenPricing";

// TEMP DEBUG: shows a per-turn (and per-iteration, for turns that resolved a
// navigate_to_company_search call) token/cost breakdown, so it's possible to
// see exactly where a turn's cost came from without manually instrumenting
// the network tab. Toggled from AgentChat's header bug icon. Remove this
// whole file (and its wiring in AgentChat.component.js / AgentChatContext.js)
// once the Managed Agent's cost profile is no longer under active tuning.

function formatCost(cost) {
  return `$${cost.toFixed(4)}`;
}

function UsageRow({ label, usage, model, bold }) {
  const cost = computeTurnCost(usage, model);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1.3fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr",
        gap: 0.5,
        fontSize: "0.7rem",
        fontWeight: bold ? 700 : 400,
        py: 0.25,
      }}
    >
      <span>{label}</span>
      <span>{(usage?.input_tokens || 0).toLocaleString()}</span>
      <span>{(usage?.output_tokens || 0).toLocaleString()}</span>
      <span>{(usage?.cache_creation_input_tokens || 0).toLocaleString()}</span>
      <span>{(usage?.cache_read_input_tokens || 0).toLocaleString()}</span>
      <span>{formatCost(cost)}</span>
    </Box>
  );
}

function TurnEntry({ entry, index }) {
  const [expanded, setExpanded] = useState(false);
  const hasIterations = entry.iterations && entry.iterations.length > 1;
  const totalTokens = countTurnTokens(entry.usage);
  const totalCost = computeTurnCost(entry.usage, entry.model);

  return (
    <Box sx={{ borderBottom: "1px solid #eee", py: 1 }}>
      <Box
        onClick={() => hasIterations && setExpanded((v) => !v)}
        sx={{ cursor: hasIterations ? "pointer" : "default" }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#444",
            fontWeight: 600,
            wordBreak: "break-word",
          }}
        >
          #{index + 1} {hasIterations ? (expanded ? "▾" : "▸") : ""} {entry.message}
        </Typography>
        <Typography variant="caption" sx={{ color: "#888", display: "block" }}>
          {totalTokens.toLocaleString()} tokens · {formatCost(totalCost)}
          {hasIterations ? ` · ${entry.iterations.length} model calls` : ""}
        </Typography>
      </Box>
      {hasIterations ? (
        <Collapse in={expanded}>
          <Box sx={{ mt: 0.5, backgroundColor: "#f9f9fa", borderRadius: 1, p: 0.75 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.3fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr",
                gap: 0.5,
                fontSize: "0.65rem",
                color: "#999",
                borderBottom: "1px solid #e2e2e2",
                pb: 0.25,
                mb: 0.25,
              }}
            >
              <span>call</span>
              <span>in</span>
              <span>out</span>
              <span>cache w</span>
              <span>cache r</span>
              <span>cost</span>
            </Box>
            {entry.iterations.map((it, i) => (
              <UsageRow
                key={i}
                model={entry.model}
                usage={it.usage}
                label={
                  it.toolUses && it.toolUses.length
                    ? `${i + 1}. ${it.toolUses
                        .map((tu) => tu.name + (tu.isError ? " (error)" : ""))
                        .join(", ")}`
                    : `${i + 1}. ${it.stopReason || "final"}`
                }
              />
            ))}
            <Box sx={{ borderTop: "1px solid #e2e2e2", mt: 0.25, pt: 0.25 }}>
              <UsageRow label="total" usage={entry.usage} model={entry.model} bold />
            </Box>
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
}

function AgentChatDebugPanel({ debugLog, height }) {
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [debugLog]);

  return (
    <Paper
      elevation={6}
      sx={{
        width: 320,
        height,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#333",
          color: "#fff",
          px: 1.5,
          py: 1,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Token usage debug
        </Typography>
      </Box>
      <Box ref={listRef} sx={{ flexGrow: 1, overflowY: "auto", px: 1.5 }}>
        {!debugLog || debugLog.length === 0 ? (
          <Typography variant="caption" sx={{ color: "#888", display: "block", mt: 2 }}>
            No requests yet this conversation.
          </Typography>
        ) : (
          debugLog.map((entry, i) => <TurnEntry key={i} entry={entry} index={i} />)
        )}
      </Box>
    </Paper>
  );
}

export default AgentChatDebugPanel;
